import React, { createContext, useContext, useState, useMemo, ReactNode } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider, Theme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

// ============================================================================
// BRAND CONFIGURATION - Easy to customize for white-labeling
// ============================================================================
export interface BrandConfig {
  name: string;
  shortName: string;
  tagline: string;
  logo?: string;
  version: string;
}

// ============================================================================
// THEME CONFIGURATION TYPES
// ============================================================================
export interface ThemeColors {
  // Primary colors
  primary: string;
  primaryDark: string;
  primaryLight: string;

  // Secondary/Accent colors
  secondary: string;
  secondaryDark: string;
  secondaryLight: string;

  // Sidebar colors
  sidebarBg: string;
  sidebarBgGradient: string;
  sidebarText: string;
  sidebarTextMuted: string;
  sidebarActiveItem: string;
  sidebarHoverItem: string;
  /**
   * The active-item rail and icon tint. Kept as its own token rather than
   * reusing `info`: the sidebar sits on a dark surface where `info` (tuned for
   * light backgrounds) is often too dim to read as "selected". Each preset
   * supplies a value that is legible against its own sidebar.
   */
  sidebarAccent: string;
  /** Gradient form of `sidebarAccent`, used for the active rail and the mark. */
  sidebarAccentGradient: string;

  // Header colors
  headerBg: string;
  headerBgGradient: string;
  headerText: string;

  // Background colors
  background: string;
  backgroundPaper: string;
  backgroundAlt: string;

  // Text colors
  textPrimary: string;
  textSecondary: string;

  // Status colors
  success: string;
  warning: string;
  error: string;
  info: string;

  // Other
  divider: string;
  border: string;
}

export interface ThemeConfig {
  id: string;
  name: string;
  colors: ThemeColors;
  brand: BrandConfig;
}

// ============================================================================
// PRESET THEMES - Add more themes here for white-labeling
// ============================================================================

const defaultBrand: BrandConfig = {
  name: 'Palbon ERP',
  shortName: 'Palbon',
  tagline: 'AI-enabled ERP for growing businesses',
  logo: '/logo192.png',
  version: '1.0.0',
};

/**
 * Palbon — the brand theme, and the default.
 *
 * These values are the app-token spelling of `pages/landing/landingTokens.ts`,
 * so the authenticated shell reads as the same product as the landing page and
 * the login screen a user just came through. They are duplicated rather than
 * imported: `landingTokens` deliberately does not depend on the theme system
 * (a logged-out visitor must always see Palbon brand regardless of whatever
 * preset sits in their localStorage), and importing it here would create the
 * cycle that separation exists to avoid.
 *
 * Keep the two in sync by hand if the brand palette moves. The shared values
 * are ink #05060f, the hero gradient, and the #38bdf8 -> #818cf8 -> #a78bfa ramp.
 */
export const palbonTheme: ThemeConfig = {
  id: 'palbon',
  name: 'Palbon',
  brand: defaultBrand,
  colors: {
    // #0284c7 rather than the brighter #38bdf8: primary lands on white
    // surfaces as button and link text, where #38bdf8 is ~1.9:1 and fails
    // WCAG AA. The bright accent is reserved for dark surfaces and fills.
    primary: '#0284c7',
    primaryDark: '#0369a1',
    primaryLight: '#38bdf8',

    secondary: '#7c3aed',
    secondaryDark: '#6d28d9',
    secondaryLight: '#a78bfa',

    sidebarBg: '#05060f',
    sidebarBgGradient: [
      'radial-gradient(600px 300px at 50% -5%, #1e3a8a 0%, rgba(30,58,138,0) 60%)',
      'radial-gradient(500px 400px at 100% 30%, #4c1d95 0%, rgba(76,29,149,0) 55%)',
      'linear-gradient(180deg, #05060f 0%, #0a1024 45%, #0d1b3e 100%)',
    ].join(', '),
    sidebarText: '#f8fafc',
    sidebarTextMuted: 'rgba(226,232,240,0.72)',
    sidebarActiveItem: 'rgba(56,189,248,0.12)',
    sidebarHoverItem: 'rgba(255,255,255,0.06)',
    sidebarAccent: '#38bdf8',
    sidebarAccentGradient: 'linear-gradient(180deg, #38bdf8 0%, #818cf8 50%, #a78bfa 100%)',

    headerBg: '#05060f',
    headerBgGradient: 'linear-gradient(135deg, #0a1024 0%, #05060f 55%, #0d1b3e 100%)',
    headerText: '#f8fafc',

    background: '#f6f8fc',
    backgroundPaper: '#ffffff',
    backgroundAlt: '#eff6ff',

    textPrimary: '#0f172a',
    textSecondary: '#475569',

    success: '#16a34a',
    warning: '#f59e0b',
    error: '#dc2626',
    info: '#0284c7',

    divider: '#e2e8f0',
    border: '#e2e8f0',
  },
};

// Blue Theme (Current - Professional)
export const blueTheme: ThemeConfig = {
  id: 'blue',
  name: 'Professional Blue',
  brand: defaultBrand,
  colors: {
    primary: '#1976d2',
    primaryDark: '#1565c0',
    primaryLight: '#42a5f5',

    secondary: '#9c27b0',
    secondaryDark: '#7b1fa2',
    secondaryLight: '#ba68c8',

    sidebarBg: '#1a237e',
    sidebarBgGradient: 'linear-gradient(180deg, #1a237e 0%, #0d47a1 50%, #1565c0 100%)',
    sidebarText: '#ffffff',
    sidebarTextMuted: 'rgba(255, 255, 255, 0.7)',
    sidebarActiveItem: 'rgba(255, 255, 255, 0.15)',
    sidebarHoverItem: 'rgba(255, 255, 255, 0.08)',
    sidebarAccent: '#e3f2fd',
    sidebarAccentGradient: 'linear-gradient(180deg, #ffffff 0%, #bbdefb 100%)',

    headerBg: '#1976d2',
    headerBgGradient: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
    headerText: '#ffffff',

    background: '#f5f7fa',
    backgroundPaper: '#ffffff',
    backgroundAlt: '#e3f2fd',

    textPrimary: '#212121',
    textSecondary: '#757575',

    success: '#4caf50',
    warning: '#ff9800',
    error: '#f44336',
    info: '#2196f3',

    divider: '#e0e0e0',
    border: '#e0e0e0',
  },
};

// Dark Theme
export const darkTheme: ThemeConfig = {
  id: 'dark',
  name: 'Dark Mode',
  brand: defaultBrand,
  colors: {
    primary: '#90caf9',
    primaryDark: '#42a5f5',
    primaryLight: '#e3f2fd',

    secondary: '#ce93d8',
    secondaryDark: '#ab47bc',
    secondaryLight: '#f3e5f5',

    sidebarBg: '#1e1e1e',
    sidebarBgGradient: 'linear-gradient(180deg, #1e1e1e 0%, #2d2d2d 50%, #1e1e1e 100%)',
    sidebarText: '#ffffff',
    sidebarTextMuted: 'rgba(255, 255, 255, 0.7)',
    sidebarActiveItem: 'rgba(144, 202, 249, 0.2)',
    sidebarHoverItem: 'rgba(255, 255, 255, 0.08)',
    sidebarAccent: '#90caf9',
    sidebarAccentGradient: 'linear-gradient(180deg, #90caf9 0%, #64b5f6 100%)',

    headerBg: '#272727',
    headerBgGradient: 'linear-gradient(135deg, #272727 0%, #1e1e1e 100%)',
    headerText: '#ffffff',

    background: '#121212',
    backgroundPaper: '#1e1e1e',
    backgroundAlt: '#2d2d2d',

    textPrimary: '#ffffff',
    textSecondary: '#b0b0b0',

    success: '#66bb6a',
    warning: '#ffa726',
    error: '#ef5350',
    info: '#29b6f6',

    divider: '#424242',
    border: '#424242',
  },
};

// Green Theme (Eco/Nature)
export const greenTheme: ThemeConfig = {
  id: 'green',
  name: 'Nature Green',
  brand: defaultBrand,
  colors: {
    primary: '#2e7d32',
    primaryDark: '#1b5e20',
    primaryLight: '#4caf50',

    secondary: '#00897b',
    secondaryDark: '#00695c',
    secondaryLight: '#26a69a',

    sidebarBg: '#1b5e20',
    sidebarBgGradient: 'linear-gradient(180deg, #1b5e20 0%, #2e7d32 50%, #388e3c 100%)',
    sidebarText: '#ffffff',
    sidebarTextMuted: 'rgba(255, 255, 255, 0.7)',
    sidebarActiveItem: 'rgba(255, 255, 255, 0.15)',
    sidebarHoverItem: 'rgba(255, 255, 255, 0.08)',
    sidebarAccent: '#ffffff',
    sidebarAccentGradient: 'linear-gradient(180deg, #ffffff 0%, #c8e6c9 100%)',

    headerBg: '#2e7d32',
    headerBgGradient: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 50%, #1b5e20 100%)',
    headerText: '#ffffff',

    background: '#f1f8e9',
    backgroundPaper: '#ffffff',
    backgroundAlt: '#c8e6c9',

    textPrimary: '#212121',
    textSecondary: '#757575',

    success: '#4caf50',
    warning: '#ff9800',
    error: '#f44336',
    info: '#2196f3',

    divider: '#c8e6c9',
    border: '#a5d6a7',
  },
};

// Purple Theme (Creative)
export const purpleTheme: ThemeConfig = {
  id: 'purple',
  name: 'Creative Purple',
  brand: defaultBrand,
  colors: {
    primary: '#7b1fa2',
    primaryDark: '#4a148c',
    primaryLight: '#ab47bc',

    secondary: '#e91e63',
    secondaryDark: '#c2185b',
    secondaryLight: '#f06292',

    sidebarBg: '#4a148c',
    sidebarBgGradient: 'linear-gradient(180deg, #4a148c 0%, #6a1b9a 50%, #7b1fa2 100%)',
    sidebarText: '#ffffff',
    sidebarTextMuted: 'rgba(255, 255, 255, 0.7)',
    sidebarActiveItem: 'rgba(255, 255, 255, 0.15)',
    sidebarHoverItem: 'rgba(255, 255, 255, 0.08)',
    sidebarAccent: '#ce93d8',
    sidebarAccentGradient: 'linear-gradient(180deg, #e1bee7 0%, #ba68c8 100%)',

    headerBg: '#7b1fa2',
    headerBgGradient: 'linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 50%, #4a148c 100%)',
    headerText: '#ffffff',

    background: '#f3e5f5',
    backgroundPaper: '#ffffff',
    backgroundAlt: '#e1bee7',

    textPrimary: '#212121',
    textSecondary: '#757575',

    success: '#4caf50',
    warning: '#ff9800',
    error: '#f44336',
    info: '#2196f3',

    divider: '#e1bee7',
    border: '#ce93d8',
  },
};

// Orange Theme (Energy)
export const orangeTheme: ThemeConfig = {
  id: 'orange',
  name: 'Energy Orange',
  brand: defaultBrand,
  colors: {
    primary: '#f57c00',
    primaryDark: '#e65100',
    primaryLight: '#ffb74d',

    secondary: '#0288d1',
    secondaryDark: '#01579b',
    secondaryLight: '#4fc3f7',

    sidebarBg: '#e65100',
    // Final stop pulled from #f57c00 to #e65100. At #f57c00 even pure white
    // reaches only 2.70:1, so this preset's own white nav labels failed AA at
    // the bottom of the panel regardless of the active-item treatment.
    sidebarBgGradient: 'linear-gradient(180deg, #bf360c 0%, #d84e00 50%, #e65100 100%)',
    sidebarText: '#ffffff',
    sidebarTextMuted: 'rgba(255, 255, 255, 0.7)',
    sidebarActiveItem: 'rgba(255, 255, 255, 0.15)',
    sidebarHoverItem: 'rgba(255, 255, 255, 0.08)',
    sidebarAccent: '#ffffff',
    sidebarAccentGradient: 'linear-gradient(180deg, #ffffff 0%, #ffe0b2 100%)',

    headerBg: '#f57c00',
    headerBgGradient: 'linear-gradient(135deg, #f57c00 0%, #e65100 50%, #bf360c 100%)',
    headerText: '#ffffff',

    background: '#fff3e0',
    backgroundPaper: '#ffffff',
    backgroundAlt: '#ffe0b2',

    textPrimary: '#212121',
    textSecondary: '#757575',

    success: '#4caf50',
    warning: '#ff9800',
    error: '#f44336',
    info: '#2196f3',

    divider: '#ffe0b2',
    border: '#ffcc80',
  },
};

// All available themes
export const themes: ThemeConfig[] = [
  palbonTheme,
  blueTheme,
  darkTheme,
  greenTheme,
  purpleTheme,
  orangeTheme,
];

// ============================================================================
// CREATE MUI THEME FROM CONFIG
// ============================================================================
export const createAppTheme = (config: ThemeConfig): Theme => {
  const { colors } = config;
  const isDark = config.id === 'dark';

  return createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: {
        main: colors.primary,
        dark: colors.primaryDark,
        light: colors.primaryLight,
      },
      secondary: {
        main: colors.secondary,
        dark: colors.secondaryDark,
        light: colors.secondaryLight,
      },
      success: {
        main: colors.success,
      },
      warning: {
        main: colors.warning,
      },
      error: {
        main: colors.error,
      },
      info: {
        main: colors.info,
      },
      background: {
        default: colors.background,
        paper: colors.backgroundPaper,
      },
      text: {
        primary: colors.textPrimary,
        secondary: colors.textSecondary,
      },
      divider: colors.divider,
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontWeight: 600 },
      h2: { fontWeight: 600 },
      h3: { fontWeight: 600 },
      h4: { fontWeight: 600 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 8,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
            },
          },
        },
      },
    },
  });
};

// ============================================================================
// THEME CONTEXT
// ============================================================================
interface ThemeContextType {
  currentTheme: ThemeConfig;
  setTheme: (themeId: string) => void;
  availableThemes: ThemeConfig[];
  colors: ThemeColors;
  brand: BrandConfig;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// ============================================================================
// THEME PROVIDER COMPONENT
// ============================================================================
interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: string;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'palbon'
}) => {
  const [currentThemeId, setCurrentThemeId] = useState(() => {
    // Try to get saved theme from localStorage
    const saved = localStorage.getItem('app-theme');
    return saved || defaultTheme;
  });

  const currentTheme = useMemo(() => {
    return themes.find(t => t.id === currentThemeId) || palbonTheme;
  }, [currentThemeId]);

  const muiTheme = useMemo(() => {
    return createAppTheme(currentTheme);
  }, [currentTheme]);

  const setTheme = (themeId: string) => {
    setCurrentThemeId(themeId);
    localStorage.setItem('app-theme', themeId);
  };

  const contextValue: ThemeContextType = {
    currentTheme,
    setTheme,
    availableThemes: themes,
    colors: currentTheme.colors,
    brand: currentTheme.brand,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// ============================================================================
// HOOK TO USE THEME
// ============================================================================
export const useAppTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within a ThemeProvider');
  }
  return context;
};

// Export default theme for backward compatibility
export default createAppTheme(blueTheme);
